// TERM8 (docs/build-briefs/TERM8-reader-tipping.md; DEC-335): the tip suggestions a location's card reader shows, and the
// bounds an Admin's choices must stay inside. One module, so no number appears anywhere else.
//
// The defaults are the owner's ruling (DEC-335, 2026-10-09; percentages changed to 20 / 22 / 25 by TIPDEF1, 2026-10-25, to match the ticket presets): three percentages plus Custom and No tip
// on the reader, and for a total under the smart tip threshold the reader shows three fixed amounts instead,
// $1 / $2 / $3. Percentages are whole percent; fixed amounts and the threshold are cents.
//
// The bounds are the PRODUCT's, not Stripe's: Stripe's Terminal Configuration docs state no numeric limits for these
// values (https://docs.stripe.com/terminal/features/collecting-tips/on-reader), so the bounds only keep an obvious
// typo (a 2500% tip, a $0.00 button) off a guest's screen. Currency is USD because the product is US only; the
// currency key sent to the provider lives here for the same reason.

export const READER_TIP_CURRENCY = "usd" as const;

/** The reader shows exactly three suggestions of each kind. */
export const READER_TIP_CHOICE_COUNT = 3;

export interface ReaderTipSettings {
  /** Whole percent, strictly ascending, exactly READER_TIP_CHOICE_COUNT. Shown at or above the threshold. */
  percentages: readonly number[];
  /** Cents, strictly ascending, exactly READER_TIP_CHOICE_COUNT. Shown below the threshold. */
  fixedAmountsCents: readonly number[];
  /** Cents. Below this pre-tip total the reader shows the fixed amounts; at or above it, the percentages. */
  smartThresholdCents: number;
}

export const READER_TIP_DEFAULTS: ReaderTipSettings = Object.freeze({
  percentages: Object.freeze([20, 22, 25]),
  fixedAmountsCents: Object.freeze([100, 200, 300]),
  smartThresholdCents: 1000,
});

export const READER_TIP_BOUNDS = Object.freeze({
  percentageMin: 1,
  percentageMax: 100,
  fixedAmountCentsMin: 1,
  fixedAmountCentsMax: 100_000,
  smartThresholdCentsMin: 1,
  smartThresholdCentsMax: 1_000_000,
});

/** PAY1-S4-F9. The threshold actually sent to the provider. Stripe shows the three fixed amounts when the pre-tip total
 * is BELOW `smart_tip_threshold` and the three percentages at or above it
 * (https://docs.stripe.com/terminal/features/collecting-tips/on-reader, "Suggest smart tips"). The owner wants the
 * location's percentages at every subtotal, so the sync sends the smallest positive threshold: any tip-eligible amount
 * (a positive number of cents, else the reader skips the tip screen) is at or above it. The location's own saved
 * threshold is therefore not sent. */
export const READER_TIP_ALWAYS_PERCENT_THRESHOLD_CENTS = 1;
